/**
 * GL PRODUCTION wiring for the WHY-capture mount (GL-1 §1/§3), reconciled to
 * the server-owned wire contract (docs/gl-api-wire-contract.md,
 * QLearn-service) after the ROW1 activation gate:
 *
 * - B3 cure: the apiService transport resolves every non-200 to
 *   {errStatus, errMsg} with the server's typed {glError, retryable} NESTED
 *   inside errMsg — the component contract is a TOP-LEVEL glError, so the old
 *   passthrough made every backend denial render as a fake success ("Hint
 *   shown." on a 403). `requestHint` now adapts the transport shape to the
 *   typed contract; there is no success path without a served hint.
 * - B4/B5 cure: postWhy and markAssisted are GONE. The student's reasoning
 *   rides ONLY the card-draft call at grade time (gl-shared/gl-api.draftCard);
 *   "assisted" is recorded server-side at hint-serve. The event sink forwards
 *   ONLY the client-originated WHY-funnel rows — hint/card/deck rows are
 *   server-emitted, and the ingest drops client copies anyway.
 *
 * Importing this module registers the production event sink exactly once. It is
 * dormant when GL is off (the component returns null and emits nothing), so this
 * has no observable effect on a flag-off build.
 */
import { api } from "../../services/apiService";
import { glErrorForStatus } from "../gl-shared/gl-fetch";
import { setGlEventSink } from "./gl-events";

/** The client-originated rows the server ingest accepts (wire-contract row 9). */
export const CLIENT_INGEST_EVENTS = ["gl.why.submitted", "gl.why.reasked", "gl.why.skipped"];

/**
 * Adapt the apiService non-200 shape to the GL typed-error contract: prefer the
 * server's own typed body (nested under errMsg), fall back to the HTTP-status
 * map, and treat a "success" without hint text as unavailable — a denial can
 * never render as a served hint again.
 */
const adaptHintResponse = (response) => {
  if (!response) {
    return { glError: "unavailable", retryable: true };
  }
  if (response.errStatus !== undefined && response.errStatus !== null) {
    const typed = response.errMsg;
    if (typed && typeof typed === "object" && typed.glError) {
      return typed;
    }
    return { glError: glErrorForStatus(Number(response.errStatus)), retryable: true };
  }
  if (response.glError || typeof response.hintText === "string") {
    return response;
  }
  return { glError: "unavailable", retryable: true };
};

/** Real transport for the mount's `glApi` prop (WHY never rides a hint call). */
export const glApi = {
  requestHint: ({ questionId, hintIndex }) =>
    Promise.resolve(api.requestGlHint({ questionId, hintIndex })).then(adaptHintResponse)
};

/** Production sink: POST allowlisted WHY-funnel records to /gl/event. Rows the
 *  ingest would drop anyway (server-emitted vocabulary) are filtered here so
 *  exam-volume hint events don't generate pointless requests. Never called when
 *  GL is off (no events are emitted). */
export const glEventSink = (record) => {
  if (!record || !CLIENT_INGEST_EVENTS.includes(record.event)) {
    return;
  }
  // Fire-and-forget as a single promise chain: Promise.resolve().then()
  // absorbs a SYNCHRONOUS transport throw into the rejection path (the sink is
  // called from component handlers, which must never see it), and the one
  // .catch absorbs any rejection. (A promise is always truthy — Sonar S6544.)
  Promise.resolve()
    .then(() => api.postGlEvent(record))
    .catch(() => {});
};

export { getUserIdHash } from "./gl-identity";

// One-time production registration (idempotent; last writer wins). Tests that
// need a spy sink import gl-events directly and call setGlEventSink themselves.
setGlEventSink(glEventSink);
